import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../api';
import { FRECUENCIA_GLOBAL_OPTIONS, labelFrecuencia } from './informeIvaFrequencyOptions';

const DIAS_SEMANA = [
  { v: 0, label: 'Domingo' },
  { v: 1, label: 'Lunes' },
  { v: 2, label: 'Martes' },
  { v: 3, label: 'Miércoles' },
  { v: 4, label: 'Jueves' },
  { v: 5, label: 'Viernes' },
  { v: 6, label: 'Sábado' }
];

function fmtDigestHorario(cfg) {
  const d = DIAS_SEMANA.find((x) => x.v === Number(cfg.digestCronDayOfWeek ?? 1));
  const h = String(cfg.digestCronHour ?? 8).padStart(2, '0');
  const m = String(cfg.digestCronMinute ?? 0).padStart(2, '0');
  return `${d?.label || '—'} ${h}:${m}`;
}

export default function InformeIvaAdministracionPage() {
  const now = new Date();
  const [mesKpi, setMesKpi] = useState(now.getMonth() + 1);
  const [anioKpi, setAnioKpi] = useState(now.getFullYear());
  const [cfg, setCfg] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadCfg = useCallback(async () => {
    const { data } = await api.get('/informes-iva/config/global');
    setCfg(data);
  }, []);

  const loadResumen = useCallback(async () => {
    const { data } = await api.get('/informes-iva/administracion/resumen', {
      params: { mes: mesKpi, anio: anioKpi }
    });
    setResumen(data);
  }, [mesKpi, anioKpi]);

  useEffect(() => {
    loadCfg().catch((e) => toast.error(e.response?.data?.error || e.message));
  }, [loadCfg]);

  useEffect(() => {
    loadResumen().catch(() => setResumen(null));
  }, [loadResumen]);

  const saveCfg = async () => {
    if (!cfg) return;
    if (cfg.autoSendGlobalEnabled && !window.confirm('¿Activar AUTO_SEND global? Solo tras validación completa.')) {
      return;
    }
    if (cfg.modoOperacion === 'PRODUCCION' && !window.confirm('¿Pasar a PRODUCCIÓN? Se permitirán envíos reales a clientes.')) {
      return;
    }
    const ini = Number(cfg.periodoIvaDiaInicio ?? 4);
    const fin = Number(cfg.periodoIvaDiaCierre ?? 18);
    if (fin <= ini) {
      toast.error('El día de cierre debe ser posterior al día de inicio');
      return;
    }
    setBusy(true);
    try {
      await api.put('/informes-iva/config/global', {
        modoOperacion: cfg.modoOperacion || 'PRUEBA',
        frecuenciaGlobalInformes: cfg.frecuenciaGlobalInformes || 'SEMANAL',
        digestMode: cfg.digestMode,
        digestPorContadorEnabled: cfg.digestPorContadorEnabled,
        digestDestinoPruebaEmail: cfg.digestDestinoPruebaEmail,
        digestScheduleEnabled: cfg.digestScheduleEnabled,
        digestCronDayOfWeek: cfg.digestCronDayOfWeek,
        digestCronHour: cfg.digestCronHour,
        digestCronMinute: cfg.digestCronMinute,
        scheduledCronHour: cfg.scheduledCronHour,
        scheduledCronMinute: cfg.scheduledCronMinute,
        scheduledMaxEjecucionesMes: cfg.scheduledMaxEjecucionesMes,
        periodoIvaDiaInicio: cfg.periodoIvaDiaInicio,
        periodoIvaDiaCierre: cfg.periodoIvaDiaCierre,
        persistInformeFiles: cfg.persistInformeFiles,
        emailOficina: cfg.emailOficina,
        frescuraHoras: cfg.frescuraHoras,
        timezone: cfg.timezone,
        retencionDias: cfg.retencionDias,
        autoSyncOnRun: cfg.autoSyncOnRun,
        syncF29OnRun: cfg.syncF29OnRun,
        scheduledRunsEnabled: cfg.scheduledRunsEnabled,
        autoSendGlobalEnabled: cfg.autoSendGlobalEnabled,
        permitirRemanenteCeroAsumido: cfg.permitirRemanenteCeroAsumido
      });
      toast.success('Configuración guardada');
      await loadCfg();
      await loadResumen();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const enviarDigest = async () => {
    const prueba = (cfg?.modoOperacion || 'PRUEBA') !== 'PRODUCCION' || !cfg?.digestPorContadorEnabled;
    if (!window.confirm(prueba ? '¿Enviar correo resumen (digest) de prueba?' : '¿Enviar digest a contadores/oficina?')) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/informes-iva/digest/send/${anioKpi}/${mesKpi}`);
      toast.success(`Enviado (${data.mode || '—'}, OK: ${data.sent ?? 0})`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const ejecutarCron = async () => {
    if (!window.confirm('¿Buscar ahora qué empresas tocan informe automático y encolarlas?')) return;
    setBusy(true);
    try {
      const { data } = await api.post('/informes-iva/jobs/scheduled-run');
      if (data.skipped) toast.message(`Nada encolado: ${data.skipped}`);
      else toast.success(`${data.enqueued ?? 0} informe(s) encolados`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) {
    return <div className="max-w-4xl mx-auto p-8 text-slate-500">Cargando administración…</div>;
  }

  const modoPrueba = (cfg.modoOperacion || 'PRUEBA') !== 'PRODUCCION';
  const digestProduccionActivo = !modoPrueba && Boolean(cfg.digestPorContadorEnabled);
  const ventana = resumen?.ventanaPeriodo;
  const freqGlobalLabel = labelFrecuencia(cfg.frecuenciaGlobalInformes || resumen?.frecuenciaGlobalInformes || 'SEMANAL');

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-8">
      <div>
        <p className="text-sm text-slate-500">
          <Link to="/informes-iva" className="text-indigo-600 hover:underline">
            ← Panel del mes
          </Link>
        </p>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">Administración Informes IVA</h1>
      </div>

      <section className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-5 text-sm text-slate-800 space-y-3">
        <p className="font-semibold text-indigo-950">Dos cosas distintas (no las mezcle)</p>
        <div>
          <p className="font-medium">1. Informe IVA (por empresa)</p>
          <p className="text-slate-700 mt-1">
            Descarga datos del SII, calcula IVA y guarda el resultado. Puede enviarse al cliente como PDF. Usted lo dispara en el panel
            o el sistema lo hace <strong>cada X días</strong> según la frecuencia (global o por empresa en Cliente → frecuencia).
          </p>
        </div>
        <div>
          <p className="font-medium">2. Digest (correo resumen)</p>
          <p className="text-slate-700 mt-1">
            <strong>No es el informe del cliente.</strong> Es un solo correo para usted (prueba) o para contadores (producción) con una
            lista: qué empresas tienen datos viejos, sin cálculo, pendientes de revisión, etc. Sirve para monitorear el estudio, no para
            declarar IVA.
          </p>
        </div>
      </section>

      <section
        className={`rounded-xl border-2 p-5 ${modoPrueba ? 'bg-amber-50 border-amber-400' : 'bg-emerald-50 border-emerald-500'}`}
      >
        <p className={`text-lg font-bold ${modoPrueba ? 'text-amber-950' : 'text-emerald-950'}`}>
          {modoPrueba ? 'MODO PRUEBA' : 'MODO PRODUCCIÓN'}
        </p>
        <p className="text-sm mt-2 text-slate-800">
          {modoPrueba
            ? 'Informes al cliente bloqueados. Digest solo al correo de prueba.'
            : 'Informes al cliente permitidos. Digest puede ir a contadores si lo activa abajo.'}
        </p>
        <label className="block mt-3 max-w-xs text-sm font-medium">
          Ambiente
          <select
            className="w-full border rounded-lg px-3 py-2 mt-1 bg-white"
            value={cfg.modoOperacion || 'PRUEBA'}
            onChange={(e) => setCfg({ ...cfg, modoOperacion: e.target.value })}
          >
            <option value="PRUEBA">PRUEBA</option>
            <option value="PRODUCCION">PRODUCCIÓN</option>
          </select>
        </label>
      </section>

      {resumen && ventana?.mensaje && (
        <p className={`text-sm p-3 rounded-lg ${ventana.skipScheduledCron ? 'bg-slate-100' : 'bg-indigo-50'}`}>{ventana.mensaje}</p>
      )}

      <section className="bg-white border rounded-xl p-6 space-y-4 text-sm">
        <h2 className="font-semibold text-slate-900">Informes automáticos (sync SII + cálculo)</h2>
        <p className="text-slate-700 leading-relaxed">
          No se genera un informe <strong>todos los días</strong>. Una vez al día, a la hora que indique, el sistema pregunta: «¿a esta
          empresa ya le toca según su frecuencia?». Si pasaron los días (ej. 7 = semanal), encola un informe nuevo.
        </p>

        <label className="block max-w-md font-medium">
          Frecuencia global (empresas sin frecuencia propia)
          <select
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={cfg.frecuenciaGlobalInformes || 'SEMANAL'}
            onChange={(e) => setCfg({ ...cfg, frecuenciaGlobalInformes: e.target.value })}
          >
            {FRECUENCIA_GLOBAL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <p className="text-xs text-slate-500">
          Por empresa: en el panel → <strong>Cliente</strong> → «Frecuencia del informe». «Usar global» = {freqGlobalLabel}. «Solo manual»
          = nunca automático.
        </p>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={Boolean(cfg.scheduledRunsEnabled)} onChange={(e) => setCfg({ ...cfg, scheduledRunsEnabled: e.target.checked })} />
          Activar informes automáticos
        </label>

        <div className="rounded-lg border bg-slate-50 p-4 space-y-3">
          <p className="font-medium text-slate-800">Ventana del mes calendario</p>
          <div className="grid sm:grid-cols-2 gap-3 text-xs">
            <label>
              Día de inicio (desde aquí puede correr el automático)
              <input type="number" min={1} max={28} className="w-full border rounded-lg px-2 py-2 mt-1 bg-white" value={cfg.periodoIvaDiaInicio ?? 4} onChange={(e) => setCfg({ ...cfg, periodoIvaDiaInicio: Number(e.target.value) })} />
            </label>
            <label>
              Día de cierre (desde aquí el mes tributario objetivo es el mes en curso)
              <input type="number" min={2} max={28} className="w-full border rounded-lg px-2 py-2 mt-1 bg-white" value={cfg.periodoIvaDiaCierre ?? 18} onChange={(e) => setCfg({ ...cfg, periodoIvaDiaCierre: Number(e.target.value) })} />
            </label>
          </div>
          <p className="text-slate-600 text-xs">
            Ej. inicio 4, cierre 18: días 1–3 nada; 4–17 informe del mes anterior; desde 18 del mes en curso.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 max-w-md text-xs">
          <label>
            Hora del control diario (solo mira quién toca, no calcula a todas)
            <input type="number" min={0} max={23} className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.scheduledCronHour ?? 6} onChange={(e) => setCfg({ ...cfg, scheduledCronHour: Number(e.target.value) })} />
          </label>
          <label>
            Minuto
            <input type="number" min={0} max={59} className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.scheduledCronMinute ?? 0} onChange={(e) => setCfg({ ...cfg, scheduledCronMinute: Number(e.target.value) })} />
          </label>
        </div>
        <label className="block max-w-xs text-xs">
          Máx. informes automáticos por empresa y mes tributario
          <input type="number" min={1} max={31} className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.scheduledMaxEjecucionesMes ?? 8} onChange={(e) => setCfg({ ...cfg, scheduledMaxEjecucionesMes: Number(e.target.value) })} />
        </label>
      </section>

      <section className="bg-white border rounded-xl p-6 space-y-4 text-sm">
        <h2 className="font-semibold text-slate-900">Digest — correo resumen (monitoreo)</h2>
        <p className="text-slate-600 text-xs">Programado: {fmtDigestHorario(cfg)} ({cfg.timezone || 'America/Santiago'})</p>

        <div className="grid md:grid-cols-2 gap-4">
          <div className={`rounded-xl border-2 p-4 ${modoPrueba ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-slate-50 opacity-80'}`}>
            <p className="font-semibold text-amber-950">Digest prueba {modoPrueba ? '· ACTIVO' : '· inactivo'}</p>
            <p className="text-xs mt-1 text-slate-700">Un mail con asunto [Prueba]. Lista de empresas a revisar. Sin informe PDF adjunto masivo.</p>
            <label className="block text-xs mt-2">
              Email (vacío = super admin)
              <input type="email" className="w-full border rounded-lg px-2 py-2 mt-1 bg-white" value={cfg.digestDestinoPruebaEmail || ''} onChange={(e) => setCfg({ ...cfg, digestDestinoPruebaEmail: e.target.value })} disabled={!modoPrueba} />
            </label>
          </div>
          <div className={`rounded-xl border-2 p-4 ${digestProduccionActivo ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`}>
            <p className="font-semibold text-emerald-950">Digest producción {digestProduccionActivo ? '· ACTIVO' : '· inactivo'}</p>
            <p className="text-xs mt-1 text-slate-700">Requiere PRODUCCIÓN + casilla abajo. Envía a contadores u oficina.</p>
            <label className="flex items-center gap-2 text-xs mt-2">
              <input type="checkbox" checked={Boolean(cfg.digestPorContadorEnabled)} disabled={modoPrueba} onChange={(e) => setCfg({ ...cfg, digestPorContadorEnabled: e.target.checked })} />
              Enviar digest a contadores / oficina
            </label>
            {cfg.digestPorContadorEnabled && !modoPrueba && (
              <>
                <select className="w-full border rounded-lg px-2 py-2 mt-2 text-xs" value={cfg.digestMode || 'POR_ASIGNADO'} onChange={(e) => setCfg({ ...cfg, digestMode: e.target.value })}>
                  <option value="POR_ASIGNADO">Por contador IVA</option>
                  <option value="GLOBAL_OFICINA">Correo oficina</option>
                  <option value="AMBOS">Ambos</option>
                </select>
                <input type="email" placeholder="Email oficina" className="w-full border rounded-lg px-2 py-2 mt-2 text-xs" value={cfg.emailOficina || ''} onChange={(e) => setCfg({ ...cfg, emailOficina: e.target.value })} />
              </>
            )}
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={cfg.digestScheduleEnabled !== false} onChange={(e) => setCfg({ ...cfg, digestScheduleEnabled: e.target.checked })} />
          Programar digest automático (día/hora abajo)
        </label>
        <div className="grid sm:grid-cols-3 gap-3 max-w-lg text-xs">
          <label>
            Día semana
            <select className="w-full border rounded-lg px-2 py-2 mt-1" value={Number(cfg.digestCronDayOfWeek ?? 1)} onChange={(e) => setCfg({ ...cfg, digestCronDayOfWeek: Number(e.target.value) })}>
              {DIAS_SEMANA.map((d) => (
                <option key={d.v} value={d.v}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Hora
            <input type="number" min={0} max={23} className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.digestCronHour ?? 8} onChange={(e) => setCfg({ ...cfg, digestCronHour: Number(e.target.value) })} />
          </label>
          <label>
            Minuto
            <input type="number" min={0} max={59} className="w-full border rounded-lg px-2 py-2 mt-1" value={cfg.digestCronMinute ?? 0} onChange={(e) => setCfg({ ...cfg, digestCronMinute: Number(e.target.value) })} />
          </label>
        </div>
        <button type="button" disabled={busy} onClick={enviarDigest} className="px-4 py-2 border rounded-lg text-sm">
          Enviar digest ahora
        </button>
      </section>

      <section className="bg-white border rounded-xl p-6 space-y-3 text-sm">
        <h2 className="font-semibold text-slate-900">Opciones avanzadas</h2>
        <details className="text-xs text-slate-600">
          <summary className="cursor-pointer font-medium text-slate-800">Frescura, retención, zona horaria, archivos en disco</summary>
          <div className="mt-3 space-y-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={Boolean(cfg.persistInformeFiles)} onChange={(e) => setCfg({ ...cfg, persistInformeFiles: e.target.checked })} />
              Guardar PDF/HTML en VPS (off = solo BD)
            </label>
            <label className="flex items-center gap-2 text-red-900">
              <input type="checkbox" checked={Boolean(cfg.autoSendGlobalEnabled)} onChange={(e) => setCfg({ ...cfg, autoSendGlobalEnabled: e.target.checked })} />
              AUTO_SEND global
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={Boolean(cfg.autoSyncOnRun)} onChange={(e) => setCfg({ ...cfg, autoSyncOnRun: e.target.checked })} />
              Sync SII al pulsar Informe IVA (manual)
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={cfg.syncF29OnRun !== false} onChange={(e) => setCfg({ ...cfg, syncF29OnRun: e.target.checked })} />
              Incluir F29 en sync
            </label>
            <div className="grid sm:grid-cols-3 gap-2 pt-2">
              <label>
                Frescura (h)
                <input type="number" className="w-full border rounded px-2 py-1 mt-1" value={cfg.frescuraHoras ?? 48} onChange={(e) => setCfg({ ...cfg, frescuraHoras: Number(e.target.value) })} />
              </label>
              <label>
                Retención (días)
                <input type="number" className="w-full border rounded px-2 py-1 mt-1" value={cfg.retencionDias ?? 365} onChange={(e) => setCfg({ ...cfg, retencionDias: Number(e.target.value) })} />
              </label>
              <label>
                Zona horaria
                <input type="text" className="w-full border rounded px-2 py-1 mt-1" value={cfg.timezone || 'America/Santiago'} onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })} />
              </label>
            </div>
          </div>
        </details>
        <div className="flex flex-wrap gap-2 pt-2">
          <button type="button" disabled={busy} onClick={saveCfg} className="px-4 py-2 bg-slate-900 text-white rounded-lg disabled:opacity-50">
            Guardar
          </button>
          <button type="button" disabled={busy} onClick={ejecutarCron} className="px-4 py-2 border rounded-lg disabled:opacity-50">
            Probar informes automáticos ahora
          </button>
        </div>
      </section>
    </div>
  );
}
